import {
    POST_LIST_TYPES,
    PostcardActionAttributes,
    PostcardActionButtonsHandlersType,
    PostcardDataType,
    PostComponentOptionsType,
} from "../../../../Components/Post/Post.types";
import {
    CardBoxed,
    PostCardType,
    PostComponentsSection,
    PostImageGrid,
} from "../../../../Components/Post/Post.styles";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { PostCommentPreviewSection } from "../../../../Components/Post/PostCommentPreviewSection";
import ImageGrid, {
    IMAGE_GRID_TYPES,
    ImageType,
} from "../../../../Components/ImageGrid/ImageGrid";
import { SERVICE_MODALS_OPTIONS_STATES } from "../../../../Components/Modals/ServiceFunctionsModal.types";
import { Box, Stack } from "@mui/material";
import { TextExpander } from "@shared/TextExpander/TextExpander";
import { PostActionButtons } from "../../../../Components/Post/PostActions/PostActionButtons";
import { UserType } from "@shared/types";
import { useWindowSize } from "@infrastructure/Hooks/useWindowSize";
import { PostHeaderDefault } from "../../../../Components/Post/Header/PostHeaderDefault";
import {
    InfoContainer,
    InfoTypography,
} from "../../../../Components/Post/PostForm/PostForm.styles";
import { usePostDetailsModalStore } from "../../../../Components/Modals/PostDetailsModal/PostDetailsModalStore";
import {
    addSubscriber,
    removeSubscriber,
} from "@infrastructure/Events/EventBus";
import { OPEN_PRESENTER } from "../../../../Components/Post/PostForm/events";
import { useInView } from "@infrastructure/Hooks/useInView";
import { PostReactionsListModal } from "../../../../Components/Modals/ServiceModals/ReactionsListModal/PostReactionsListModal";
import { PostPoll } from "../../../../Components/Post/PostPoll/PostPoll";
import { TextMarkup } from "@shared/Components/TextMarkup/TextMarkup";
import { useIsCurrentUserId } from "@infrastructure/Hooks/UserInteractions/useUserId";
import { PollTypesEnum } from "@shared/Types/Polls";
import { shortAndShareEntityOnMobile } from "@infrastructure/Helpers/SharingEntity";
import { useGroupSelector } from "../../../../Components/GroupSelector/useGroupSelector";
import { LocationButton } from "src/Presentation/Components/Post/PostActions/LocationButton";
import { LinkoutButton } from "src/Presentation/Components/Post/PostActions/LinkoutButton";

const linkoutLocationButtonStyles = {
    textColor: "var(--community-color-primary-text)",
    backgroundColor: "var(--community-color-transparent-light-steal-blue-10)",
    rightIconColor: "var(--community-color-primary-text)",
    leftIconColor: "var(--community-color-light-steal-blue)",
};

export function TruckPost({
    id,
    data,
    showGroupName,
    postActions,
    listType = POST_LIST_TYPES.POST_DETAILS,
    closeButtonCallback,
}: PostComponentOptionsType) {
    const { isMobileScreenSizeFlag } = useWindowSize();

    const postActionOpts: PostcardActionAttributes = {
        postId: data.id,
        postData: data.postData as PostcardDataType,
        ...((postActions.onCommentButtonClick
            ? { onCommentButtonClick: postActions.onCommentButtonClick }
            : {}) as PostcardActionButtonsHandlersType),
        showCommentButton: data.pageRelated?.showCommentButton,
        isInsidePostDetails: postActions.isInsidePostDetails,
        commentButtonText: "Kommentieren",
        createReactOnPost: postActions.createReactOnPost,
        onReportButtonClick: onReportButtonClick,
        showReportOption: !data.status.isEditorial,
        onShareButtonClicked,
    };

    const selectedPost: Pick<
        PostComponentOptionsType,
        "id" | "data" | "postActions"
    > = {
        id: id,
        data: data,
        postActions: postActions,
    };

    function onSettingsClickHandler(e: React.MouseEvent<HTMLElement>) {
        e.stopPropagation();
        postActions.setSelectedPost(selectedPost);
        postActions.setIsPostOptionsModalOpen(true);
        postActions.setModalCurrentAction(
            SERVICE_MODALS_OPTIONS_STATES.POST_STARTING_OPTIONS,
        );
    }

    function onPostGroupTitleClickHandler(e: React.MouseEvent<HTMLElement>) {
        e.stopPropagation();
        listType !== POST_LIST_TYPES.POST_DETAILS &&
            listType !== POST_LIST_TYPES.POST_DETAILS_FULLSCREEN &&
            postActions.onCardContentClickHandler?.(id.toString());
    }

    const cardContentPostOpts = useMemo(() => {
        return {
            ...(postActions.onCardContentClickHandler
                ? {
                      onClick: () => {
                          postActions.onCardContentClickHandler?.(data.id);
                      },
                  }
                : {}),
        };
    }, [postActions, data]);

    function openCommentPreviewOwnerAvatarModal(
        e: React.MouseEvent<HTMLElement>,
    ) {
        e.stopPropagation();
        postActions.onAvatarClick?.(data.latestComment?.createdBy as UserType);
    }

    const isOwnPost = useIsCurrentUserId(data.user.uuid);

    const commentPreviewSectionProps = {
        post: data,
        handleCommentPreviewOptionsClicked:
            postActions.handleCommentPreviewOptionsClicked,
        handleCommentPreviewReportClicked:
            postActions.handleCommentPreviewReportClicked,
        createReactOnPreviewComment: postActions.createReactOnPreviewComment,
        openCommentPreviewOwnerAvatarModal: openCommentPreviewOwnerAvatarModal,
        onCommentButtonClick: postActions.onCommentButtonClick,
        showCommentTeaser: postActions.showCommentTeaser && !isOwnPost,
    };

    function onReportButtonClick() {
        postActions.setSelectedPost &&
            postActions.setSelectedPost(selectedPost);
        if (postActions && postActions.setIsPostOptionsModalOpen) {
            postActions.setIsPostOptionsModalOpen(true);
            postActions.setModalCurrentAction(
                SERVICE_MODALS_OPTIONS_STATES.REPORTING_CONTENT,
            );
        }
    }

    const { mainGroupId } = useGroupSelector();
    function onShareButtonClicked() {
        mainGroupId &&
            shortAndShareEntityOnMobile(
                mainGroupId,
                id.toString(),
                data.group!.name,
            );
    }

    useEffect(() => {
        if (listType === POST_LIST_TYPES.POST_DETAILS) {
            postActions?.setHeaderComponent?.(
                <PostHeaderDefault
                    openPostAvatarModal={onPostGroupTitleClickHandler}
                    onSettingsClickHandler={onSettingsClickHandler}
                    user={data.user}
                    group={data.group}
                    postData={data.postData}
                    onLocationClick={onPostGroupTitleClickHandler}
                    showGroupName={showGroupName}
                    onPostGroupTitleClickHandler={onPostGroupTitleClickHandler}
                    showClosingButton={true}
                    isHighlighted={data.status.isHighlighted}
                    handleCloseForHeaderComponent={
                        postActions?.handleCloseForHeaderComponent
                    }
                />,
            );
            return () => {
                postActions?.setHeaderComponent?.(undefined);
            };
        }
    }, []);

    function prepareHeader(listType: POST_LIST_TYPES) {
        if (listType !== POST_LIST_TYPES.POST_DETAILS) {
            return (
                <PostHeaderDefault
                    openPostAvatarModal={onPostGroupTitleClickHandler}
                    onSettingsClickHandler={onSettingsClickHandler}
                    user={data.user}
                    group={data.group}
                    postData={data.postData}
                    onLocationClick={onPostGroupTitleClickHandler}
                    showGroupName={showGroupName}
                    onPostGroupTitleClickHandler={onPostGroupTitleClickHandler}
                    showClosingButton={!!closeButtonCallback}
                    handleCloseForHeaderComponent={closeButtonCallback}
                    isHighlighted={data.status.isHighlighted}
                />
            );
        }
    }

    const { isPostDetailsModalOpen } = usePostDetailsModalStore();
    const { ref, inView } = useInView();
    const videoRef = useRef<HTMLVideoElement>();

    const [onlyPlayOnce, setOnlyPlayOnce] = useState(postActions.autoPlayVideo);
    useEffect(() => {
        if (isPostDetailsModalOpen || !inView) {
            if (onlyPlayOnce) {
                videoRef.current?.play();
            } else if (!inView) {
                videoRef.current?.pause();
            }
        }
    }, [inView, isPostDetailsModalOpen, onlyPlayOnce]);

    useEffect(() => {
        if (!onlyPlayOnce) {
            return;
        }

        const handlePlaying = () => {
            setOnlyPlayOnce(false);
        };

        const videoElement = videoRef?.current;

        videoElement?.addEventListener("playing", handlePlaying);

        return () => {
            videoElement?.removeEventListener("playing", handlePlaying);
        };
    }, []);

    useEffect(() => {
        const subscriber = addSubscriber(OPEN_PRESENTER, () => {
            videoRef.current?.pause();
        });
        return () => {
            removeSubscriber(subscriber);
        };
    }, []);

    const [showReactionsModal, setShowReactionsModal] = useState(false);

    return (
        <>
            {showReactionsModal && (
                <PostReactionsListModal
                    open={showReactionsModal}
                    setOpen={setShowReactionsModal}
                    postId={data.id}
                />
            )}
            <CardBoxed
                mt={
                    listType === POST_LIST_TYPES.POST_DETAILS_FULLSCREEN
                        ? "8px"
                        : 0
                }
                isBoxed={data.pageRelated?.boxed}
                {...cardContentPostOpts}
                type={
                    data.status.isHighlighted
                        ? PostCardType.highlighted
                        : PostCardType.normal
                }
                sx={{
                    ...(data.pageRelated?.boxed && {
                        boxShadow: "rgba(0, 0, 0, 0.15) 0px 2px 8px",
                    }),
                }}
            >
                {prepareHeader(listType)}
                <Box>
                    {!data.pageRelated?.hideImages &&
                        data.postData.images &&
                        data.postData.images?.length > 0 && (
                            <PostImageGrid>
                                <ImageGrid
                                    images={
                                        data.postData.images ??
                                        ([] as ImageType[])
                                    }
                                    onClickEach={
                                        !postActions.isInsidePostDetails
                                            ? () => {
                                                  return;
                                              }
                                            : undefined
                                    }
                                    imageGridType={
                                        postActions.isInsidePostDetails &&
                                        isMobileScreenSizeFlag
                                            ? IMAGE_GRID_TYPES.SMALL
                                            : IMAGE_GRID_TYPES.DEFAULT
                                    }
                                />
                            </PostImageGrid>
                        )}
                    {data.postData.videos &&
                        data.postData.videos?.length > 0 &&
                        !data.postData.isMediaUnderProcessing && (
                            <PostImageGrid
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                }}
                                ref={ref}
                            >
                                <video
                                    poster={
                                        data.postData.videos[0].thumbnailUrl
                                    }
                                    ref={videoRef}
                                    controls
                                    controlsList={"nodownload noplaybackrate"}
                                    preload={"none"}
                                    playsInline
                                    style={{
                                        background: "black",
                                        maxHeight: "80vh",
                                        width: "100%",
                                        minWidth: "100%",
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                    }}
                                >
                                    <source src={data.postData.videos[0].url} />
                                </video>
                            </PostImageGrid>
                        )}
                    {data.postData.isMediaUnderProcessing &&
                        listType === POST_LIST_TYPES.POST_DETAILS && (
                            <InfoContainer marginTop="16px">
                                <InfoTypography>
                                    Dein Video wird im Hintergrund verarbeitet
                                    und für die Ausgabe vorbereitet. Dein
                                    Beitrag ist für andere Nutzer erst nach
                                    Fertigstellung sichtbar.
                                </InfoTypography>
                            </InfoContainer>
                        )}
                    {!!data.postData.content &&
                        data.postData.poll?.type !==
                            PollTypesEnum.POLL_RATING && (
                            <PostComponentsSection>
                                <TextExpander
                                    lineClampValue={
                                        postActions.isInsidePostDetails ? 20 : 6
                                    }
                                >
                                    <TextMarkup
                                        textToMarkup={data.postData.content}
                                    />
                                </TextExpander>
                            </PostComponentsSection>
                        )}
                    {data.postData?.poll?.pollOptions?.length && (
                        <PostPoll
                            postId={data.id}
                            data={data.postData?.poll}
                            content={data.postData?.content}
                        />
                    )}
                </Box>
                <Stack marginTop="12px">
                    {postActionOpts.isInsidePostDetails ? (
                        postActionOpts.postData?.linkoutUrl && (
                            <LinkoutButton
                                linkoutUrl={postActionOpts.postData?.linkoutUrl}
                                {...linkoutLocationButtonStyles}
                            />
                        )
                    ) : (
                        <>
                            {postActionOpts.postData?.location && (
                                <LocationButton
                                    location={postActionOpts.postData?.location}
                                    {...linkoutLocationButtonStyles}
                                />
                            )}
                            {postActionOpts.postData?.linkoutUrl && (
                                <LinkoutButton
                                    linkoutUrl={
                                        postActionOpts.postData?.linkoutUrl
                                    }
                                    {...linkoutLocationButtonStyles}
                                />
                            )}
                        </>
                    )}
                </Stack>
                {!data.pageRelated?.hideActions && (
                    <PostComponentsSection>
                        <PostActionButtons
                            {...postActionOpts}
                            user={data.user}
                            postReactions={data.postData.postReactions}
                            pollsOptions={data.postData.poll?.pollOptions}
                        />
                    </PostComponentsSection>
                )}
                {data.pageRelated?.showPreviewComment && (
                    <PostCommentPreviewSection
                        {...commentPreviewSectionProps}
                    />
                )}
            </CardBoxed>
        </>
    );
}
